import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, ExternalLink, Clock, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
const APPLE_EMBED = 'https://embed.podcasts.apple.com/gb/podcast/will-the-%2465bln-unilever-mccormick-food-deal-create-value/id1665680837?i=1000774175673';
const APPLE_LINK = 'https://podcasts.apple.com/gb/podcast/will-the-%2465bln-unilever-mccormick-food-deal-create-value/id1665680837?i=1000774175673';
const COVER_ART = 'https://cdn.magicpatterns.com/uploads/6wjHdezjBHwsSX1Zdoajif/gres.png';
interface Publication {
  publication: string;
  date: string;
  title: string;
  byline: string;
  summary: string;
  url: string;
  image?: string;
}
// Add future publications here (most recent first) — each entry renders automatically.
const publications: Publication[] = [{
  publication: 'Chair X',
  date: '2019 – 2025',
  title: 'Hydrogen: From Hype to Pragmatism (2019–2025)',
  byline: 'Dr Richard M Oblath FIMMM, Chair of the IOM3 Energy Transition Group',
  summary: 'A retrospective tracing the evolution of the hydrogen economy from the exuberant optimism of 2019 to a more sober, pragmatic outlook by 2025 — examining where green hydrogen and its derivatives have genuinely gained traction, where expectations outpaced reality, and what a realistic role for hydrogen looks like across the hardest-to-abate sectors.',
  url: 'https://acrobat.adobe.com/id/urn:aaid:sc:EU:04bc087f-83c2-42fa-80b3-0095dd15c66e'
}, {
  publication: 'IOM3 Materials World',
  date: '9 October 2023',
  title: 'Transforming the Energy System to Hit Net-Zero',
  byline: 'Dr Richard M Oblath FIMMM, Chair of the IOM3 Energy Transition Group',
  summary: 'Written a year on from the previous piece, this article takes stock of global and UK progress toward the 2050 net-zero target ahead of COP28 — covering the state of clean energy investment, the pace of renewable and nuclear deployment, the continuing role of oil and gas during the transition, and the specific policy and infrastructure gaps (grid capacity, storage, hydrogen) that need urgent attention in the UK.',
  url: 'https://www.iom3.org/resource/transforming-the-energy-system-to-hit-net-zero.html',
  image: 'https://cdn.magicpatterns.com/uploads/4vtPTTcnQeusRQ3ASNQ9xd/2098567a-631a-4856-bd70-d92c22037899.jpg'
}, {
  publication: 'IOM3 Materials World',
  date: '5 May 2022',
  title: 'Age of Uncertainty – Energy Security and Transitioning to Net-Zero',
  byline: 'Dr Richard M Oblath FIMMM, Chair of the IOM3 Energy Transition Group',
  summary: 'Written against the backdrop of the invasion of Ukraine and the resulting energy price shock, this article argues that short-term energy security pressures should not derail the longer-term, slower-moving transition to a lower-emission global energy system, and reflects on how the two challenges need to be tackled in parallel rather than in competition with one another.',
  url: 'https://www.iom3.org/resource/age-of-uncertainty-energy-security-and-transitioning-to-net-zero.html',
  image: 'https://cdn.magicpatterns.com/uploads/nuGunp3shzgkZSjgLdoCKZ/yfjm.png'
}];
const VISIBLE_COUNT = 4;
function PublicationCard({
  item
}: {
  item: Publication;
}) {
  return <article className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 py-10" data-id="element-174">
      {/* Publication wordmark + optional featured image */}
      <div className="md:col-span-3" data-id="element-175">
        {item.image && <img src={item.image} alt={`${item.byline.split(',')[0]} — featured in ${item.publication}`} loading="lazy" decoding="async" className="w-28 h-28 object-cover object-center rounded-lg mb-4 ring-1 ring-white/15" data-id="element-176" />}
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-gray-300 border border-white/20 rounded px-3 py-2" data-id="element-177">
          {item.publication}
        </span>
        <p className="text-xs text-gray-400 mt-3" data-id="element-178">{item.date}</p>
      </div>

      {/* Citation content */}
      <div className="md:col-span-9" data-id="element-179">
        <h3 className="font-serif text-xl md:text-2xl leading-snug mb-2" data-id="element-180">
          {item.title}
        </h3>
        <p className="text-sm font-medium text-gray-300 mb-1" data-id="element-181">{item.byline}</p>
        <p className="text-xs uppercase tracking-wider text-gray-400 mb-4" data-id="element-182">
          {item.publication} · {item.date}
        </p>
        <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-5" data-id="element-183">
          {item.summary}
        </p>
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-gold hover:text-gold-light transition-colors" data-id="element-184">
          Read Article
          <ExternalLink size={15} data-id="element-185" />
        </a>
      </div>
    </article>;
}
export function Publications() {
  const [expanded, setExpanded] = useState(false);
  const hasMore = publications.length > VISIBLE_COUNT;
  const primary = hasMore ? publications.slice(0, VISIBLE_COUNT) : publications;
  const overflow = hasMore ? publications.slice(VISIBLE_COUNT) : [];
  return <section id="media" className="relative py-24 overflow-hidden bg-navy text-offwhite" data-id="element-186">
      {/* Atmospheric artwork background */}
      <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center scale-110 blur-md opacity-60" style={{
      backgroundImage: `url(${COVER_ART})`
    }} data-id="element-187"></div>
      <div aria-hidden="true" className="absolute inset-0 bg-navy/80" data-id="element-188"></div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10" data-id="element-189">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-190">
          <div className="mb-16" data-id="element-191">
            <h2 className="font-serif text-3xl md:text-4xl mb-6" data-id="element-192">
              Media &amp; Publications
            </h2>
            <div className="w-16 h-px bg-gold" data-id="element-193"></div>
          </div>

          {/* Podcast appearance */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-id="element-194">
            {/* Cover art */}
            <div className="lg:col-span-5" data-id="element-195">
              <motion.img initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.7
            }} src={COVER_ART} alt="The Mergers & Acquisitions Podcast with Guus Greve" loading="lazy" decoding="async" className="w-full h-auto rounded-2xl shadow-2xl ring-1 ring-white/10" data-id="element-196" />
            </div>

            {/* Details + player */}
            <div className="lg:col-span-7" data-id="element-197">
              <div className="flex items-center space-x-3 mb-5" data-id="element-198">
                <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-gold" data-id="element-199">
                  <Mic size={14} data-id="element-200" />
                  Podcast Appearance
                </span>
              </div>

              <h3 className="font-serif text-2xl md:text-3xl leading-snug mb-4" data-id="element-201">
                Will the $65bln Unilever–McCormick Food Deal Create Value?
              </h3>

              <p className="text-gray-300 text-sm font-medium mb-6" data-id="element-202">
                The Mergers &amp; Acquisitions Podcast · Season 3, Episode 4
              </p>

              <p className="text-gray-400 leading-relaxed mb-8" data-id="element-203">
                Host Guus Greve is joined by his former Shell colleague and
                current Non-Executive Director, Richard Oblath, to discuss and
                analyse the recently announced Unilever–McCormick deal —
                applying lessons from prior episodes to examine whether the
                transaction will ultimately create shareholder value.
              </p>

              <div className="flex flex-wrap gap-6 mb-8 text-sm text-gray-400" data-id="element-204">
                <span className="inline-flex items-center gap-2" data-id="element-205">
                  <Calendar size={16} className="text-gold" data-id="element-206" />
                  28 June 2026
                </span>
                <span className="inline-flex items-center gap-2" data-id="element-207">
                  <Clock size={16} className="text-gold" data-id="element-208" />
                  27 minutes
                </span>
              </div>

              <a href={APPLE_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 border border-gray-500 text-offwhite font-medium hover:border-gold hover:text-gold transition-colors duration-300 mb-8" data-id="element-209">
                Listen on Apple Podcasts
                <ExternalLink size={16} data-id="element-210" />
              </a>

              <div className="bg-white/5 p-4 backdrop-blur-sm border border-white/10 rounded-xl" data-id="element-211">
                <iframe title="Richard Oblath on The Mergers & Acquisitions Podcast — Unilever-McCormick deal" allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write" frameBorder="0" loading="lazy" height="175" style={{
                width: '100%',
                overflow: 'hidden',
                borderRadius: '10px'
              }} sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" src={APPLE_EMBED} data-id="element-212" />
                <p className="text-xs text-gray-400 mt-3 text-center" data-id="element-213">
                  Press play to listen without leaving the page.
                </p>
              </div>
            </div>
          </div>

          {/* Publications & thought leadership */}
          <div className="mt-20 pt-16 border-t border-white/10" data-id="element-214">
            <h3 className="font-serif text-2xl md:text-3xl mb-4" data-id="element-215">
              Publications &amp; Thought Leadership
            </h3>
            <p className="text-gray-400 md:text-lg mb-10 max-w-2xl" data-id="element-216">
              Selected commentary on energy security, decarbonization, and the
              energy transition.
            </p>

            <div className="divide-y divide-white/10 border-t border-white/10" data-id="element-217">
              {primary.map((item, idx) => <PublicationCard key={idx} item={item} data-id="element-218" />)}

              <AnimatePresence data-id="element-219">
                {expanded && overflow.map((item, idx) => <motion.div key={`overflow-${idx}`} initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="overflow-hidden border-t border-white/10" data-id="element-220">
                      <PublicationCard item={item} data-id="element-221" />
                    </motion.div>)}
              </AnimatePresence>
            </div>

            {hasMore && <button onClick={() => setExpanded(!expanded)} className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-offwhite hover:text-gold transition-colors" data-id="element-222">
                {expanded ? 'Show Fewer' : `View All Publications (${publications.length})`}
                {expanded ? <ChevronUp size={16} data-id="element-223" /> : <ChevronDown size={16} data-id="element-224" />}
              </button>}
          </div>
        </motion.div>
      </div>
    </section>;
}